import { useSelector } from "react-redux";
import styled from "styled-components";

import medallionsImage from "assets/enhancements.svg";
import BattleRating from "components/base/BattleRating";
import {
  CategoryGroup,
  CategoryName,
  CategorySeparator,
  screenItemMixin,
} from "components/base/DataScreenBase";
import ItemIcon from "components/base/ItemIcon";
import SkillIcon from "components/base/SkillIcon";
import Tooltip, { TooltipDesc, TooltipName } from "components/base/Tooltip";
import TooltipStats from "components/base/TooltipStats";
import { BASE_STATS_INFO } from "data/baseStats";
import { getPlatinumBattleRatingCount } from "libs/fight";
import {
  MAX_BASE_STAT_VALUE,
  getAreaData,
  getPartData,
  getPilotData,
  getPlanetData,
  getShipData,
} from "libs/stats";
import { getPlayerDetails } from "redux/selectors/gameSelectors";
import { CharacterBaseStats, PartTypes, UpgradeModel } from "types";
import { COLORS } from "utils/constants";

import { media } from "components/styles/breakpoints";
import AvatarShip from "./AvatarShip";
import { getShipUpgradesData } from "libs/item";

interface BarValueProps {
  percentage: number;
  color: string;
}

interface BarIconProps {
  image: string;
  color: string;
}

interface ComponentProps {
  color: string;
}

const PlayerDetailsWrapper = styled.div`
  font-size: 14px;

  ${media.desktop`
    font-size: 18px;
  `}
`;

const MainInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  ${screenItemMixin}

  ${media.desktop`
    gap: 12px;
  `}
`;

const VitalsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const Name = styled.div`
  text-transform: uppercase;
  font-weight: 600;
  font-size: 20px;

  ${media.desktop`
    font-size: 24px;
  `}
`;

const Medallions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  color: ${COLORS.ENHANCEMENTS};
`;

const MedallionsIcon = styled.div<{ image: string }>`
  height: 16px;
  width: 16px;
  mask: url(${(props) => props.image}) no-repeat center;
  background-color: ${COLORS.ENHANCEMENTS};
`;

const Ratings = styled.div`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 2px;

  ${media.desktop`
    font-size: 18px;
  `}
`;

const ImagesWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  justify-content: center;
`;

const ShipImageWrapper = styled.div`
  width: 50%;
  aspect-ratio: 1/1;
  cursor: pointer;
  background-color: #ffffff10;
  border: 1px #ffffff20 solid;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AvatarWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const PilotWrapper = styled.div`
  width: 50%;
  aspect-ratio: 1/1;
  border: 3px white solid;
  background-color: #ffffff10;
  border: 1px #ffffff20 solid;
  border-radius: 4px;
  overflow: hidden;
  padding: 0;
`;

const PilotImage = styled.img``;

const LocationWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 2px;
`;

const Location = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Planet = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  color: ${(props) => props.color};
`;

const LocationSeparator = styled.div`
  opacity: 0.5;
  margin-left: 5px;
`;

const Sector = styled.div`
  display: flex;
  align-items: center;
  color: ${COLORS.LOCATION};
`;

const SectorIcon = styled.div`
  font-size: 22px;
  line-height: 16px;
`;

const DetailsInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 15px;
`;

const SectionName = styled(CategoryName)`
  font-size: 16px;
`;

const ShipStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  text-align: left;
`;

const StatWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  gap: 2%;
`;

const StatIcon = styled.div<BarIconProps>`
  height: 16px;
  width: 16px;
  background-color: ${(props) => props.color};
  mask: url(${(props) => props.image}) no-repeat center;
`;

const StatBar = styled.div`
  height: 16px;
  transform: skew(-30deg);
  flex: 1 1;
  background-color: #ffffff15;
  position: relative;
  display: flex;
  align-items: center;
`;

const BarValue = styled.div.attrs(({ percentage }: BarValueProps) => ({
  style: {
    width: `${percentage}%`,
  },
}))<BarValueProps>`
  height: 100%;
  z-index: 2;
  background-color: ${(props) => props.color};
  display: flex;
  align-items: center;
`;

const UpgradesWrapper = styled.div`
  display: flex;
  gap: 5px;
`;

const Component = styled.div<ComponentProps>`
  background-color: black;
  border: 1px white solid;
  border-color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: middle;
  width: 32px;
  aspect-ratio: 1/1;
  cursor: pointer;
  border-radius: 4px;

  ${media.desktop`
    width: 44px;
  `}
`;

const SkillsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const SkillWrapper = styled.div`
  width: 32px;
  aspect-ratio: 1/1;
  font-size: 18px;

  ${media.desktop`
    width: 44px;
    font-size: 24px;
  `}
`;

export const EmptyDetails = styled.div`
  text-align: center;
  font-size: 14px;
  color: #fff;
  text-transform: uppercase;
  font-weight: 600;
  ${screenItemMixin}

  ${media.desktop`
    font-size: 18px;
  `}
`;

export default function OnlinePlayerDetails() {
  const playerDetails = useSelector(getPlayerDetails);

  if (!playerDetails) {
    return <EmptyDetails>Click on a player to view their details</EmptyDetails>;
  }

  const {
    userName,
    ship,
    pilot,
    medallions,
    location,
    installedUpgrades,
    skills,
    totalBaseStats,
    battleRatings,
    derivedStats,
    fighterData,
  } = playerDetails;

  const {
    name: shipClass,
    description: shipDesc,
    color: shipColor,
  } = getShipData(ship);
  const {
    name: pilotProfession,
    images: pilotImages,
    description: pilotDesc,
  } = getPilotData(pilot);
  const { name: planetName, color: planetColor } = getPlanetData(
    location.planet
  );
  const { name: sectorName } = getAreaData(location.area);
  const platinumRatings = getPlatinumBattleRatingCount(battleRatings);
  const shipUpgrades = getShipUpgradesData(installedUpgrades);

  return (
    <PlayerDetailsWrapper>
      <MainInfo>
        <VitalsWrapper>
          <Name>{userName}</Name>
          <Tooltip
            color={COLORS.ENHANCEMENTS}
            content={
              <>
                <TooltipName>Medallions</TooltipName>
                <TooltipDesc>
                  {`${userName} currently has ${medallions.total} total medallions.`}
                </TooltipDesc>
              </>
            }
          >
            <Medallions>
              <MedallionsIcon image={medallionsImage} />
              {medallions.total}
            </Medallions>
          </Tooltip>
          <Tooltip
            color={COLORS.RATINGS.PLATINUM}
            content={
              <>
                <TooltipName>Platinum Battle Ratings</TooltipName>
                <TooltipDesc>
                  {userName} has earned {platinumRatings} platinum battle
                  ratings.
                </TooltipDesc>
              </>
            }
          >
            <Ratings>
              <BattleRating rating="platinum" iconOnly noTooltip />
              {platinumRatings}
            </Ratings>
          </Tooltip>
          <LocationWrapper>
            <Tooltip
              color={planetColor}
              content={
                <>
                  <TooltipName>Current Location</TooltipName>
                  <TooltipDesc>
                    {userName} is currently on the planet {planetName} in the{" "}
                    {sectorName} sector.
                  </TooltipDesc>
                </>
              }
            >
              <Location>
                <Planet color={planetColor}>❍ {planetName}</Planet>
                <LocationSeparator>›</LocationSeparator>
                <Sector>
                  <SectorIcon>⌔</SectorIcon> {sectorName}
                </Sector>
              </Location>
            </Tooltip>
          </LocationWrapper>
        </VitalsWrapper>
        <ImagesWrapper>
          <ShipImageWrapper>
            <Tooltip
              color={shipColor}
              content={
                <>
                  <TooltipName>Starship: {shipClass}</TooltipName>
                  <TooltipDesc>{shipDesc}</TooltipDesc>
                </>
              }
            >
              <AvatarWrapper>
                <AvatarShip
                  ship={ship}
                  installedUpgrades={installedUpgrades}
                  isPreview
                />
              </AvatarWrapper>
            </Tooltip>
          </ShipImageWrapper>
          <PilotWrapper>
            <Tooltip
              color={COLORS.PILOTS}
              content={
                <>
                  <TooltipName>Pilot: {pilotProfession}</TooltipName>
                  <TooltipDesc>{pilotDesc}</TooltipDesc>
                </>
              }
            >
              <PilotImage src={pilotImages.avatar} />
            </Tooltip>
          </PilotWrapper>
        </ImagesWrapper>
      </MainInfo>
      <DetailsInfo>
        <CategoryGroup>
          <SectionName>
            Core Attributes
            <CategorySeparator />
          </SectionName>
          <ShipStats>
            {Object.values(BASE_STATS_INFO).map((baseStat) => {
              const baseStatSlug = baseStat.slug as keyof CharacterBaseStats;
              const statValue = totalBaseStats[baseStatSlug];

              return (
                <Tooltip
                  key={baseStatSlug}
                  color={baseStat.color}
                  content={
                    <TooltipStats
                      baseStat={baseStatSlug}
                      derivedStats={derivedStats.total.complete}
                    />
                  }
                >
                  <StatWrapper>
                    <StatIcon image={baseStat.image} color={baseStat.color} />
                    <StatBar>
                      <BarValue
                        percentage={(statValue / MAX_BASE_STAT_VALUE) * 100}
                        color={baseStat.color}
                      ></BarValue>
                    </StatBar>
                  </StatWrapper>
                </Tooltip>
              );
            })}
          </ShipStats>
        </CategoryGroup>
        <CategoryGroup>
          <SectionName>
            Ship Upgrades
            <CategorySeparator />
          </SectionName>
          <UpgradesWrapper>
            {Object.entries(shipUpgrades).map(([key, value]) => {
              const partSlug = key as PartTypes;
              const partData = getPartData(partSlug);
              const upgradeInfo = value as UpgradeModel;

              return (
                <Component key={partSlug} color={partData.color}>
                  <ItemIcon slug={upgradeInfo.slug} />
                </Component>
              );
            })}
          </UpgradesWrapper>
        </CategoryGroup>
        <CategoryGroup>
          <SectionName>
            Unlocked Skills
            <CategorySeparator />
          </SectionName>
          <SkillsWrapper>
            {skills
              .filter((skill) => !!skill.isTrained)
              .map((skill) => {
                return (
                  <SkillWrapper key={skill.slug}>
                    <SkillIcon
                      skill={skill}
                      attacker={fighterData}
                      isShowValues
                    />
                  </SkillWrapper>
                );
              })}
          </SkillsWrapper>
        </CategoryGroup>
      </DetailsInfo>
    </PlayerDetailsWrapper>
  );
}
